import { faChevronDown } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { CircularProgress } from '@material-ui/core';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AutoSizer, CellMeasurer, CellMeasurerCache, List } from 'react-virtualized';
import { ListRowProps } from 'react-virtualized/dist/es/List';
import styled from 'styled-components';
import { useAiAssistant } from '../../AiAssistantContext';
import { authSubmitToken, checkSessionActive, fetchMessages, generateTitle, IAttachment, ISSEEvent, mapApiMessages, streamChat, updateSession } from '../../api';
import { DEFAULT_MODEL } from '../../types';
import MessageBubble from './MessageBubble';
import MessageInput from './MessageInput';

interface IProps {
  sessionId: number;
  isActive?: boolean;
}

/**
 * Re-measures the row when its content resizes (e.g. image loads).
 * The outer div has a fixed height from the virtualized List style prop,
 * so we observe an inner wrapper whose height follows its content naturally.
 * When it changes we update the cache directly and tell the List to recompute.
 */
const MeasuredRow: React.FC<{
  registerChild: any;
  style: React.CSSProperties;
  rowIndex: number;
  cache: CellMeasurerCache;
  listRef: React.RefObject<List>;
  children: React.ReactNode;
}> = ({ registerChild, style, rowIndex, cache, listRef, children }) => {
  const innerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const el = innerRef.current;
    if (!el || typeof (window as any).ResizeObserver === 'undefined') return;
    const ro = new (window as any).ResizeObserver(() => {
      const newHeight = el.offsetHeight;
      const cachedHeight = cache.getHeight(rowIndex, 0);
      if (newHeight > 0 && Math.abs(newHeight - cachedHeight) > 1) {
        cache.set(rowIndex, 0, cache.getWidth(rowIndex, 0) || 1, newHeight);
        if (listRef.current) {
          listRef.current.recomputeRowHeights(rowIndex);
        }
      }
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [rowIndex, cache, listRef]);

  return (
    <div ref={(el) => { if (registerChild) registerChild(el); }} style={style}>
      <div ref={innerRef}>
        {children}
      </div>
    </div>
  );
};

const SessionView: React.FC<IProps> = ({ sessionId, isActive }) => {
  const { state, dispatch } = useAiAssistant();
  const messages = state.messagesBySession[sessionId] || [];
  const isStreaming = state.streamingSessionId === sessionId;
  const session = state.sessions.find((s) => s.id === sessionId);
  const [sessionModel, setSessionModel] = useState(session?.model || DEFAULT_MODEL);
  const abortRef = useRef<AbortController | null>(null);
  const listRef = useRef<List>(null);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const prevMessageCountRef = useRef(0);
  const prevLastContentLenRef = useRef(0);
  const awaitingAuthCodeRef = useRef(false);
  const [streamingPhase, setStreamingPhase] = useState<'waiting' | 'receiving' | null>(null);
  const streamingPhaseRef = useRef<'waiting' | 'receiving' | null>(null);
  const [statusText, setStatusText] = useState<string | null>(null);
  const [contextTokens, setContextTokens] = useState<number | null>(null);
  const [showScrollBtn, setShowScrollBtn] = useState(false);
  const isAtBottomRef = useRef(true);
  const handleSendRef = useRef<(text: string, files: File[]) => void>(() => {});
  const sessionRef = useRef(session);
  const messagesRef = useRef(messages);
  const pollCleanupRef = useRef<(() => void) | null>(null);
  const sdkSessionSavedRef = useRef(false);

  sessionRef.current = session;
  messagesRef.current = messages;

  const setPhase = (phase: 'waiting' | 'receiving' | null) => {
    streamingPhaseRef.current = phase;
    setStreamingPhase(phase);
    if (phase === null) setStatusText(null);
  };

  const cache = useMemo(
    () =>
      new CellMeasurerCache({
        fixedWidth: true,
        defaultHeight: 60,
      }),
    [],
  );

  // The messages endpoint reports the session's current context fill on the
  // last message that carries it
  const seedContextFromHistory = (raw: Array<{ contextTokens?: number }>) => {
    for (let i = raw.length - 1; i >= 0; i--) {
      if (typeof raw[i].contextTokens === 'number') {
        setContextTokens(raw[i].contextTokens as number);
        return;
      }
    }
  };

  // Load persisted messages on mount + check if agent is active
  useEffect(() => {
    let cancelled = false;
    setContextTokens(null);
    const load = async () => {
      setLoadingHistory(true);
      try {
        const raw = await fetchMessages(sessionId);
        if (!cancelled && Array.isArray(raw) && raw.length > 0) {
          dispatch({ type: 'SET_MESSAGES', sessionId, messages: mapApiMessages(raw) });
          seedContextFromHistory(raw);
        }
      } catch {
        // Failed to load history — start fresh
      }
      setLoadingHistory(false);

      // Check if agent is still working (e.g. from another tab)
      if (!cancelled) {
        const active = await checkSessionActive(sessionId);
        if (active && !cancelled) {
          dispatch({ type: 'SET_STREAMING', sessionId });
          setPhase('waiting');
          // Poll for new messages until session becomes inactive
          const poll = setInterval(async () => {
            if (cancelled) { clearInterval(poll); return; }
            try {
              const raw = await fetchMessages(sessionId);
              if (!cancelled && Array.isArray(raw) && raw.length > 0) {
                dispatch({ type: 'SET_MESSAGES', sessionId, messages: mapApiMessages(raw) });
                seedContextFromHistory(raw);
              }
              const stillActive = await checkSessionActive(sessionId);
              if (!stillActive) {
                clearInterval(poll);
                dispatch({ type: 'SET_STREAMING', sessionId: null });
                setPhase(null);
                // Final message load
                const finalRaw = await fetchMessages(sessionId);
                if (!cancelled && Array.isArray(finalRaw) && finalRaw.length > 0) {
                  dispatch({ type: 'SET_MESSAGES', sessionId, messages: mapApiMessages(finalRaw) });
                  seedContextFromHistory(finalRaw);
                }
              }
            } catch {
              clearInterval(poll);
              dispatch({ type: 'SET_STREAMING', sessionId: null });
              setPhase(null);
            }
          }, 3000);
          pollCleanupRef.current = () => { clearInterval(poll); };
        }
      }
    };
    load();
    return () => {
      cancelled = true;
      if (pollCleanupRef.current) {
        pollCleanupRef.current();
        pollCleanupRef.current = null;
        dispatch({ type: 'SET_STREAMING', sessionId: null });
        setPhase(null);
      }
    };
  }, [sessionId, dispatch]);

  // Scroll to bottom when new messages arrive or streaming text appends
  useEffect(() => {
    const count = messages.length;
    const lastMsg = count > 0 ? messages[count - 1] : null;
    const lastLen = lastMsg
      ? lastMsg.content.length + (lastMsg.activities ? lastMsg.activities.length * 100 : 0)
      : 0;

    const scrollToBottom = () => {
      requestAnimationFrame(() => {
        if (listRef.current) {
          listRef.current.recomputeRowHeights(Math.max(0, count - 1));
          listRef.current.scrollToRow(count - 1);
        }
      });
    };

    if (count !== prevMessageCountRef.current) {
      const prevCount = prevMessageCountRef.current;
      prevMessageCountRef.current = count;
      prevLastContentLenRef.current = lastLen;

      if (count - prevCount > 1) {
        // Bulk load (history) — clear cache and retry scroll after rows are measured
        cache.clearAll();
        scrollToBottom();
        // Rows near the bottom get measured only after the first scroll renders them,
        // so retry once their actual heights are known
        const retry = setTimeout(() => scrollToBottom(), 150);
        return () => clearTimeout(retry);
      } else if (count > 0 && isAtBottomRef.current) {
        cache.clear(count - 1, 0);
        scrollToBottom();
      }
    } else if (lastLen !== prevLastContentLenRef.current) {
      prevLastContentLenRef.current = lastLen;
      if (count > 0 && isAtBottomRef.current) {
        cache.clear(count - 1, 0);
        scrollToBottom();
      }
    }
  }, [messages, cache]);

  // Clear cache when session changes (messages array reference changes)
  useEffect(() => {
    cache.clearAll();
    prevMessageCountRef.current = 0;
    prevLastContentLenRef.current = 0;
    const s = state.sessions.find((ss) => ss.id === sessionId);
    if (s) setSessionModel(s.model);
  }, [sessionId, cache, state.sessions]);

  useEffect(() => {
    return () => {
      if (abortRef.current) {
        abortRef.current.abort();
        abortRef.current = null;
        dispatch({ type: 'SET_STREAMING', sessionId: null });
        setPhase(null);
      }
    };
  }, [sessionId, dispatch]);

  const handleSend = async (text: string, files: File[]) => {
    const toBase64 = (file: File): Promise<string> =>
      new Promise((resolve) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result as string);
        reader.readAsDataURL(file);
      });

    const attachments: IAttachment[] = files.length > 0
      ? await Promise.all(files.map(async (f) => ({ name: f.name, data: await toBase64(f) })))
      : [];

    const userMessage = {
      id: `user-${Date.now()}`,
      role: 'user' as const,
      content: text,
      ...(attachments.length > 0 ? {
        attachmentPreviews: attachments.map((a) =>
          a.data.startsWith('data:image/') ? a.data : a.name
        ),
      } : {}),
      timestamp: Date.now(),
    };
    dispatch({ type: 'APPEND_MESSAGE', sessionId, message: userMessage });

    // If we're waiting for an auth token, submit it instead of sending a chat message
    if (awaitingAuthCodeRef.current) {
      awaitingAuthCodeRef.current = false;
      authSubmitToken(text.trim()).then((result) => {
        dispatch({
          type: 'APPEND_MESSAGE',
          sessionId,
          message: {
            id: `auth-result-${Date.now()}`,
            role: 'assistant',
            content: result.success
              ? 'Authentication successful! You can now send messages.'
              : `Authentication failed: ${result.message}`,
            timestamp: Date.now(),
          },
        });
      }).catch((err) => {
        dispatch({
          type: 'APPEND_MESSAGE',
          sessionId,
          message: {
            id: `auth-error-${Date.now()}`,
            role: 'assistant',
            content: `Authentication error: ${err.message}`,
            timestamp: Date.now(),
          },
        });
      });
      return;
    }

    dispatch({ type: 'SET_STREAMING', sessionId });
    setPhase('waiting');

    abortRef.current = streamChat(
      sessionId,
      text,
      (event: ISSEEvent) => {
        // Persist SDK session ID on first event that carries one
        if (event.sessionId && !sdkSessionSavedRef.current) {
          sdkSessionSavedRef.current = true;
          updateSession(sessionId, { sdkSessionId: event.sessionId }).catch(() => {});
        }
        if (event.type === 'text' && event.content) {
          if (streamingPhaseRef.current === 'waiting') {
            setPhase('receiving');
          }
          const content = event.content;
          if (typeof content === 'string') {
            if (content) {
              dispatch({ type: 'APPEND_STREAMING_TEXT', sessionId, text: content });
            }
          } else if (Array.isArray(content)) {
            const text = content
              .filter((b: any) => b.type === 'text')
              .map((b: any) => b.text)
              .join('');
            if (text) {
              dispatch({ type: 'APPEND_STREAMING_TEXT', sessionId, text });
            }
            content.forEach((b: any) => {
              if (b.type === 'tool_use' && b.id && b.name) {
                dispatch({
                  type: 'ADD_TOOL_ACTIVITY',
                  sessionId,
                  activity: {
                    toolUseId: b.id,
                    toolName: b.name,
                    input: b.input || {},
                    status: 'running',
                  },
                });
              }
            });
          }
        }
        if (event.type === 'result') {
          setPhase('waiting');
        }
        if (event.type === 'context' && typeof event.contextTokens === 'number') {
          setContextTokens(event.contextTokens);
        }
        if (event.type === 'tool_use_summary') {
          dispatch({
            type: 'UPDATE_TOOL_SUMMARY',
            sessionId,
            toolUseIds: event.precedingToolUseIds || [],
            summary: event.summary || '',
          });
        }
        if (event.type === 'files_changed') {
          dispatch({ type: 'AGENT_FILES_CHANGED' });
        }
        if (event.type === 'tool_progress') {
          dispatch({
            type: 'UPDATE_TOOL_PROGRESS',
            sessionId,
            toolUseId: event.toolUseId || '',
            elapsedSeconds: event.elapsedTimeSeconds || 0,
          });
        }
        if (event.type === 'status') {
          setStatusText((event as any).status || null);
        }
        if (event.type === 'error') {
          dispatch({
            type: 'APPEND_MESSAGE',
            sessionId,
            message: {
              id: `error-${Date.now()}`,
              role: 'assistant',
              content: `Error: ${event.error || event.message || 'Unknown error'}`,
              timestamp: Date.now(),
            },
          });
        }
        if (event.type === 'rate_limit') {
          let resetStr = '';
          if (event.resetsAt) {
            const d = new Date(event.resetsAt);
            const time = d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
            const sameDay = d.toDateString() === new Date().toDateString();
            resetStr = sameDay
              ? ` Resets at ${time}.`
              : ` Resets ${d.toLocaleDateString([], { month: 'short', day: 'numeric' })} at ${time}.`;
          }
          dispatch({
            type: 'APPEND_MESSAGE',
            sessionId,
            message: {
              id: `rate-limit-${Date.now()}`,
              role: 'assistant',
              content: `You've hit your usage limit.${resetStr}`,
              timestamp: Date.now(),
            },
          });
          dispatch({ type: 'SET_STREAMING', sessionId: null });
          setPhase(null);
        }
        if (event.type === 'auth_required') {
          const isCodexSession = session?.model.startsWith('gpt-') === true;
          awaitingAuthCodeRef.current = !isCodexSession;
          const authMessage = isCodexSession
            ? 'Codex authentication is required on the server. An administrator must run `codex login --device-auth` for an available account profile.'
            : 'Authentication required. Go to [claude.ai/settings/claude-code](https://claude.ai/settings/claude-code), generate an OAuth token (`sk-ant-oat01-...`), then paste it here.';
          dispatch({
            type: 'APPEND_MESSAGE',
            sessionId,
            message: {
              id: `auth-${Date.now()}`,
              role: 'assistant',
              content: authMessage,
              timestamp: Date.now(),
            },
          });
          dispatch({ type: 'SET_STREAMING', sessionId: null });
          setPhase(null);
        }
      },
      () => {
        dispatch({ type: 'SET_STREAMING', sessionId: null });
        setPhase(null);
        abortRef.current = null;

        // Auto-name session on first response (when title is still default)
        const sess = sessionRef.current;
        if (sess && /^Chat \d+$/.test(sess.title)) {
          const all = messagesRef.current;
          const first6 = all.slice(0, 6);
          const last6 = all.slice(-6);
          const seen = new Set(first6.map((m) => m.id));
          const combined = [...first6, ...last6.filter((m) => !seen.has(m.id))];
          const msgs = combined.map((m) => ({ role: m.role, content: m.content }));
          generateTitle(msgs).then((res) => {
            if (res.title && res.title !== 'New Chat') {
              updateSession(sessionId, { title: res.title }).then((result) => {
                if (result.ok && result.data) {
                  dispatch({ type: 'UPDATE_SESSION', session: result.data });
                  dispatch({
                    type: 'APPEND_MESSAGE',
                    sessionId,
                    message: {
                      id: `system-rename-${Date.now()}`,
                      role: 'assistant',
                      content: `Chat renamed to **${res.title}**\n\nYou can regenerate the title anytime with \`/generate-title\``,
                      timestamp: Date.now(),
                    },
                  });
                }
              }).catch(() => {});
            }
          }).catch(() => {});
        }
      },
      (error) => {
        dispatch({ type: 'SET_STREAMING', sessionId: null });
        setPhase(null);
        dispatch({
          type: 'APPEND_MESSAGE',
          sessionId,
          message: {
            id: `error-${Date.now()}`,
            role: 'assistant',
            content: `Error: ${error.message}`,
            timestamp: Date.now(),
          },
        });
        abortRef.current = null;
      },
      attachments.length > 0 ? attachments : undefined,
    );
  };
  handleSendRef.current = handleSend;

  // Aborting the stream can eat messages the server already persisted: the CLI
  // finishes a turn in the same instant the user hits stop, the SSE dies before
  // the text event is consumed, and the UI never shows it (4 aug 2026: two
  // [3.2] gate tables invisible to the manager, 15 interrupt/continue turns
  // burned re-sending them). After any abort, reconcile from the server store.
  const resyncFromServer = (delaysMs: number[] = [800, 3000]) => {
    delaysMs.forEach((delay) => {
      window.setTimeout(async () => {
        try {
          const raw = await fetchMessages(sessionId);
          if (Array.isArray(raw) && raw.length > 0) {
            dispatch({ type: 'SET_MESSAGES', sessionId, messages: mapApiMessages(raw) });
            seedContextFromHistory(raw);
          }
        } catch {
          // best-effort: el reload de la sesión hace el mismo fetch
        }
      }, delay);
    });
  };

  const handleStop = () => {
    if (abortRef.current) {
      abortRef.current.abort();
      dispatch({ type: 'SET_STREAMING', sessionId: null });
      setPhase(null);
      abortRef.current = null;
      resyncFromServer();
    }
  };

  const handleModelChange = (model: string) => {
    setSessionModel(model);
    updateSession(sessionId, { model }).then((result) => {
      if (result.ok && result.data) {
        dispatch({ type: 'UPDATE_SESSION', session: result.data });
      }
    }).catch(() => {});
  };

  const handleListScroll = useCallback(
    ({ scrollTop, scrollHeight, clientHeight }: { scrollTop: number; scrollHeight: number; clientHeight: number }) => {
      const distanceFromBottom = scrollHeight - scrollTop - clientHeight;
      isAtBottomRef.current = distanceFromBottom <= 100;
      setShowScrollBtn(distanceFromBottom > 100);
    },
    [],
  );

  const handleScrollToBottom = useCallback(() => {
    if (listRef.current && messages.length > 0) {
      listRef.current.scrollToRow(messages.length - 1);
      setShowScrollBtn(false);
      isAtBottomRef.current = true;
    }
  }, [messages.length]);

  const renderRow = useCallback(
    ({ index, key, parent, style }: ListRowProps) => {
      const msg = messages[index];
      const nextMsg = messages[index + 1];
      const isLastInGroup = !nextMsg || nextMsg.role !== msg.role;
      return (
        <CellMeasurer cache={cache} columnIndex={0} key={key} rowIndex={index} parent={parent}>
          {({ registerChild }) => (
            <MeasuredRow registerChild={registerChild} style={style} rowIndex={index} cache={cache} listRef={listRef}>
              <MessageBubble
                message={msg}
                isLastInGroup={isLastInGroup}
                projectId={session?.projectId}
              />
              {index === messages.length - 1 && <div style={{ height: 16 }} />}
            </MeasuredRow>
          )}
        </CellMeasurer>
      );
    },
    [messages, cache, isStreaming],
  );

  const showEmptyState = !loadingHistory && messages.length === 0;

  return (
    <Container>
      <MessagesArea>
        {loadingHistory && (
          <LoadingHistory>
            <CircularProgress size={20} />
            <span>Loading conversation...</span>
          </LoadingHistory>
        )}
        {showEmptyState && (
          <EmptyChat>
            <EmptyIcon>{'>'}_</EmptyIcon>
            <div>Send a message to start chatting</div>
            <EmptyHint>The assistant has access to CM data via MCP tools</EmptyHint>
          </EmptyChat>
        )}
        {messages.length > 0 && (
          <ListContainer>
            <AutoSizer>
              {({ width, height }) => (
                <List
                  ref={listRef}
                  width={width}
                  height={height}
                  rowCount={messages.length}
                  rowHeight={cache.rowHeight}
                  deferredMeasurementCache={cache}
                  rowRenderer={renderRow}
                  overscanRowCount={5}
                  scrollToAlignment="end"
                  onScroll={handleListScroll}
                />
              )}
            </AutoSizer>
          </ListContainer>
        )}
        {isStreaming && streamingPhase !== null && (
          <TypingIndicator>
            <Dot $delay={0} />
            <Dot $delay={0.15} />
            <Dot $delay={0.3} />
            {statusText && <StatusLabel>{statusText}...</StatusLabel>}
          </TypingIndicator>
        )}
        {showScrollBtn && (
          <ScrollToBottomBtn onClick={handleScrollToBottom} title="Scroll to bottom">
            <FontAwesomeIcon icon={faChevronDown} />
          </ScrollToBottomBtn>
        )}
      </MessagesArea>
      <BottomBar>
        <MessageInput onSend={handleSend} onStop={handleStop} isStreaming={isStreaming} model={sessionModel} onModelChange={handleModelChange} autoFocus={isActive} contextTokens={contextTokens} />
      </BottomBar>
    </Container>
  );
};

export default SessionView;

const Container = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const MessagesArea = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  position: relative;
`;

const ListContainer = styled.div`
  flex: 1;
  padding: 0 16px;
`;

const LoadingHistory = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 40px 0;
  color: #999;
  font-size: 14px;
`;

const EmptyChat = styled.div`
  text-align: center;
  color: #999;
  margin-top: 60px;
  font-size: 14px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
`;

const EmptyIcon = styled.div`
  font-size: 28px;
  font-family: 'Courier New', monospace;
  color: #bbb;
  margin-bottom: 4px;
`;

const EmptyHint = styled.div`
  font-size: 12px;
  color: #bbb;
`;

const TypingIndicator = styled.div`
  display: flex;
  align-items: center;
  gap: 5px;
  padding: 8px 28px;
`;

const StatusLabel = styled.span`
  font-size: 12px;
  color: #999;
  margin-left: 6px;
  font-style: italic;
`;

const Dot = styled.div<{ $delay: number }>`
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #90a4ae;
  opacity: 0.4;
  animation: wave 1s ease-in-out infinite;
  animation-delay: ${(p) => p.$delay}s;
  @keyframes wave {
    0%,
    100% {
      transform: scale(1);
      opacity: 0.4;
    }
    50% {
      transform: scale(1.4);
      opacity: 1;
    }
  }
`;

const BottomBar = styled.div`
  border-top: 1px solid #eee;
  padding: 8px 16px;
`;

const ScrollToBottomBtn = styled.button`
  position: absolute;
  bottom: 8px;
  right: 24px;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  border: none;
  background: #1976d2;
  color: #fff;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
  z-index: 10;
  transition: opacity 0.15s;
  &:hover {
    background: #1565c0;
  }
`;
